import React, { useState, useEffect, useRef } from 'react';
import { BookModel, SiteSettingModel, SUBJECT_TYPES, GRADE_TYPES, BOOK_TYPES, PUBLISHERS } from '../../../../models';
import { db, storage, auth } from '../../../../firebase';
import { collection, addDoc, doc, setDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { uploadOptimizedImage } from '../../../../services/imageOptimizer';
import { Save, Info, ListOrdered, Video, Paperclip, Loader2, RotateCcw, Building2, Plus, RefreshCw, X, BookOpen } from 'lucide-react';
import BookInfo from './BookInfo';
import SetDashboard from './SetDashboard';

interface BookRegistrationProps {
    onSubmit: (book: BookModel) => void;
    siteSettings: SiteSettingModel;
    editingBook?: BookModel | null;
    onCancelEdit?: () => void;
    branchId?: string;
    isListCollapsed?: boolean;
}

type RegistrationTab = 'info' | 'analysis';

const generateUUID = () => {
    if (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID) {
        return window.crypto.randomUUID();
    }
    return Math.random().toString(36).substring(2, 15);
};

const cleanObject = (obj: any) => {
    const newObj = { ...obj };
    Object.keys(newObj).forEach(key => {
        if (newObj[key] === undefined) {
            delete newObj[key];
        }
    });
    return newObj;
};

const BookRegistration: React.FC<BookRegistrationProps> = ({ onSubmit, siteSettings, editingBook, onCancelEdit, branchId, isListCollapsed }) => {
    const [activeTab, setActiveTab] = useState<RegistrationTab>('info');
    const coverInputRef = useRef<HTMLInputElement>(null);

    const initialForm = {
        subject: '',
        grade: '',
        type: '',
        publisher: '',
        year: new Date().getFullYear().toString(),
        title: '',
    };

    const [formData, setFormData] = useState<any>(initialForm);
    const [coverFile, setCoverFile] = useState<File | null>(null);
    const [coverPreview, setCoverPreview] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [bookId, setBookId] = useState<string>('');


    // 1. Handle ID generation and Mode Switching
    useEffect(() => {
        if (editingBook) {
            setFormData({
                subject: editingBook.subject,
                grade: editingBook.grade,
                type: editingBook.type,
                publisher: editingBook.publisher,
                year: editingBook.year,
                title: editingBook.title,
            });
            setCoverPreview(editingBook.coverUrl || null);
            setBookId(editingBook.id);
            setCoverFile(null);
        } else {
            setFormData(initialForm);
            setCoverPreview(null);
            setCoverFile(null);
            setBookId(generateUUID());
        }
        setActiveTab('info');
    }, [editingBook]);

    // 2. Apply Site Settings Defaults (Only for new books, using index.ts constants if siteSettings arrays are empty)
    useEffect(() => {
        if (!editingBook) {
            const defaultSubject = siteSettings.subjects && siteSettings.subjects.length > 0 ? siteSettings.subjects[0] : SUBJECT_TYPES[0];
            const defaultGrade = siteSettings.bookGrades && siteSettings.bookGrades.length > 0 ? siteSettings.bookGrades[0] : GRADE_TYPES[0];
            const defaultType = siteSettings.bookTypes && siteSettings.bookTypes.length > 0 ? siteSettings.bookTypes[0] : BOOK_TYPES[0];
            const defaultPublisher = siteSettings.publishers && siteSettings.publishers.length > 0 ? siteSettings.publishers[0] : PUBLISHERS[0];

            setFormData((prev: any) => ({
                ...prev,
                subject: prev.subject || defaultSubject || '',
                grade: prev.grade || defaultGrade || '',
                type: prev.type || defaultType || '',
                publisher: prev.publisher || defaultPublisher || ''
            }));
        }
    }, [siteSettings, editingBook]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type } = e.target;
        if (type === 'number') {
            setFormData((prev: any) => ({
                ...prev,
                [name]: value === '' ? '' : parseInt(value, 10)
            }));
        } else {
            setFormData((prev: any) => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSelect = (name: string, value: string) => {
        setFormData((prev: any) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCoverFile = (file: File) => {
        setCoverFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
            setCoverPreview(reader.result as string);
        };
        reader.readAsDataURL(file);
    };

    const handleCoverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleCoverFile(file);
    };

    const handleRemoveCover = () => {
        setCoverFile(null);
        setCoverPreview(null);
        if (coverInputRef.current) coverInputRef.current.value = '';
    };

    const handleMainSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.title) return alert('교재 제목을 입력해주세요.');
        if (!formData.publisher) return alert('출판사를 선택해주세요.');

        setIsSaving(true);
        try {
            let coverUrl = '';
            if (coverPreview) {
                if (coverFile) {
                    const coverRef = ref(storage, `books/${bookId}/cover/${Date.now()}_${coverFile.name.replace(/\.[^/.]+$/, "")}.webp`);
                    const snapshot = await uploadOptimizedImage(coverRef, coverFile, { maxWidth: 1000, quality: 0.85 });
                    coverUrl = await getDownloadURL(snapshot.ref);
                } else {
                    coverUrl = editingBook?.coverUrl || '';
                }
            }

            const finalBook: BookModel = {
                ...formData,
                id: bookId,
                createdAt: editingBook ? (editingBook.createdAt || Date.now()) : Date.now(),
                createdBy: editingBook ? (editingBook.createdBy || 'admin') : (auth.currentUser?.uid || 'admin'),
                coverUrl: coverUrl || '',
            };

            await onSubmit(cleanObject(finalBook));

            if (!editingBook) {
                alert('기본 정보가 등록되었습니다. 다른 탭에서 추가 정보를 입력할 수 있습니다.');
            } else {
                alert('교재 정보가 업데이트되었습니다.');
            }
        } catch (error: any) {
            console.error("Save error:", error);
            alert(`데이터 저장 중 오류가 발생했습니다: ${error?.message || error}`);
        } finally {
            setIsSaving(false);
        }
    };


    return (
        <div className="w-full h-full flex flex-col bg-white">
            {/* Simple Tab Navigation */}
            <div className="flex items-center gap-6 border-b border-slate-200 px-6 shrink-0 bg-white h-12">
                <button
                    onClick={() => setActiveTab('info')}
                    className={`h-full text-sm font-bold transition-all relative flex items-center border-b-2 px-1 ${
                        activeTab === 'info' 
                            ? 'text-indigo-600 border-indigo-600' 
                            : 'text-slate-400 border-transparent hover:text-slate-600'
                    }`}
                >
                    기본 정보
                </button>
                {editingBook && (
                    <button
                        onClick={() => setActiveTab('analysis')}
                        className={`h-full text-sm font-bold transition-all relative flex items-center border-b-2 px-1 ${
                            activeTab === 'analysis' 
                                ? 'text-indigo-600 border-indigo-600' 
                                : 'text-slate-400 border-transparent hover:text-slate-600'
                        }`}
                    >
                        학습자료
                    </button>
                )}
            </div>

            {/* Tab Contents */}
            <div className="animate-in fade-in duration-300 flex-1 min-h-0 w-full flex flex-col bg-white">
                {activeTab === 'info' && (
                    <div className="space-y-6 p-6 overflow-y-auto flex-1 custom-scrollbar">
                        <BookInfo
                            formData={formData}
                            handleChange={handleChange}
                            handleSelect={handleSelect}
                            siteSettings={siteSettings}
                            coverInputRef={coverInputRef}
                            handleCoverChange={handleCoverChange}
                            onCoverDrop={handleCoverFile}
                            coverPreview={coverPreview}
                            onRemoveCover={handleRemoveCover}
                        />
                        <div className="flex justify-center gap-3">
                            {editingBook && onCancelEdit && (
                                <button type="button" onClick={onCancelEdit} className="flex items-center space-x-2 bg-slate-200 text-slate-700 px-6 py-3 rounded-xl font-bold transition-all text-sm">
                                    <RotateCcw size={16} /> <span>취소</span>
                                </button>
                            )}
                            <button
                                type="submit" disabled={isSaving}
                                onClick={handleMainSubmit}
                                className={`flex items-center space-x-2 ${editingBook ? 'bg-amber-500' : 'bg-indigo-600'} text-white px-10 py-3.5 rounded-xl font-black transition-all shadow-lg active:scale-95 disabled:bg-slate-300 text-sm`}
                            >
                                {isSaving ? <Loader2 size={20} className="animate-spin" /> : <Save size={20} />}
                                <span>{isSaving ? '저장 중...' : (editingBook ? '정보 업데이트' : '교재 등록하기')}</span>
                            </button>
                        </div>
                    </div>
                )}

                {activeTab === 'analysis' && <SetDashboard bookId={bookId} branchId={branchId} />}
            </div>

        </div>
    );
};

export default BookRegistration;
